/**
 * Hand-added stream sources, kept on the server instead of in `localStorage`.
 *
 * `sourceStorage.ts` stored them per browser, which cost two things:
 *
 *  - The URL sat in plaintext in every browser it was typed into, and an RTSP one carries
 *    the camera password in its userinfo (`rtsp://admin:pass@host/stream2`). Any XSS, any
 *    shared machine, any profile sync took the cameras with it.
 *  - The backend had no record that the feed belonged to anyone, so
 *    `backend/feed_access.py` had to allow it to any authenticated user who could name the
 *    URL — the last documented hole in the feed scoping.
 *
 * This deliberately keeps `sourceStorage`'s `Storage`-shaped interface. The load/save
 * effect pair in `App.tsx` guards a cross-group leak fixed on 2026-09-04 and its ordering
 * is documented there as load-bearing; changing what it reads from is a far smaller and
 * safer change than rewriting it.
 *
 * `backend/stream_source_routes.py` is the other half. Ownership there is per operator per
 * group — the same thing `sourcesKey(userId, groupId)` always meant — and a source is
 * watchable by its creator only.
 */
import { authHeaders } from '../../auth';
import { readSources, sourcesKey, type StoredSource } from './sourceStorage';

const API_URL = process.env.REACT_APP_API_URL || '';

/** The server half, injectable so the tests need no network. */
export interface SourceApi {
  list(): Promise<StoredSource[]>;
  replace(sources: StoredSource[]): Promise<void>;
}

export const httpSourceApi: SourceApi = {
  async list() {
    const res = await fetch(`${API_URL}/api/stream-sources`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`list failed: ${res.status}`);
    return res.json();
  },
  async replace(sources) {
    const res = await fetch(`${API_URL}/api/stream-sources`, {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(sources),
    });
    if (!res.ok) throw new Error(`replace failed: ${res.status}`);
  },
};

type LocalLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface ServerSourceStore extends LocalLike {
  /** Resolve once every pending write has reached the server. For tests and for unload. */
  flush(): Promise<void>;
  /** Internal, for hydrateSources(). */
  _adopt(key: string, sources: StoredSource[], keepLocal: boolean): void;
  _local: LocalLike;
  _api: SourceApi;
}

const WRITE_DEBOUNCE_MS = 400;

export function createServerSourceStore(
  api: SourceApi,
  local: LocalLike,
): ServerSourceStore {
  // Only hydrated keys appear here. A miss means "not loaded yet", NOT "empty" — see
  // getItem below, where the difference is load-bearing.
  const cache = new Map<string, StoredSource[]>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: Promise<void> = Promise.resolve();
  let dirtyKey: string | null = null;
  let degraded = false;   // the server could not be reached; keep using the browser copy

  function schedule(key: string) {
    dirtyKey = key;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { timer = null; void push(); }, WRITE_DEBOUNCE_MS);
  }

  async function push() {
    const key = dirtyKey;
    if (key === null) return;
    dirtyKey = null;
    const sources = cache.get(key) ?? [];
    if (degraded) {
      // No server. Persist where we can, so the session is not lost on reload.
      try { local.setItem(key, JSON.stringify(sources)); } catch { /* storage disabled */ }
      return;
    }
    try {
      await api.replace(sources);
    } catch {
      // A write that cannot reach the server falls back to the browser rather than
      // vanishing. The next hydrate adopts it again.
      degraded = true;
      try { local.setItem(key, JSON.stringify(sources)); } catch { /* storage disabled */ }
    }
  }

  const store: ServerSourceStore = {
    _local: local,
    _api: api,

    getItem(key) {
      // null, not '[]', before hydration. App.tsx's writer effect persists whatever the
      // reader saw, so answering "empty" here would overwrite the real list with nothing.
      if (!cache.has(key)) return null;
      return JSON.stringify(cache.get(key));
    },

    setItem(key, value) {
      try {
        const parsed = JSON.parse(value);
        cache.set(key, Array.isArray(parsed) ? parsed : []);
      } catch {
        cache.set(key, []);
      }
      schedule(key);
    },

    removeItem(key) {
      // An empty list must still reach the server, or a delete silently comes back.
      cache.set(key, []);
      schedule(key);
    },

    async flush() {
      if (timer) { clearTimeout(timer); timer = null; }
      pending = pending.then(push);
      await pending;
    },

    _adopt(key, sources, keepLocal) {
      cache.set(key, sources);
      degraded = keepLocal;
      if (!keepLocal) {
        // The browser copy is no longer the source of truth. Removing it is the point:
        // it is where the camera credentials were sitting.
        try { local.removeItem(key); } catch { /* storage disabled */ }
      }
    },
  };
  return store;
}

/**
 * Load one operator's list for one group, migrating the browser copy on first run.
 *
 * Three cases, and the middle one is the one that matters on the day this ships:
 *  - server has rows  → use them, drop the browser copy
 *  - server is empty but this browser still holds a list → push it up, then drop it
 *  - server unreachable → keep working from the browser copy, unchanged
 */
export async function hydrateSources(
  store: ServerSourceStore,
  userId?: number | null,
  groupId?: number | null,
): Promise<void> {
  const key = sourcesKey(userId, groupId);
  try {
    const remote = await store._api.list();
    if (remote.length === 0) {
      const localList = readSources(store._local, userId, groupId);
      if (localList.length > 0) {
        await store._api.replace(localList);
        store._adopt(key, localList, false);
        return;
      }
    }
    store._adopt(key, remote, false);
  } catch {
    store._adopt(key, readSources(store._local, userId, groupId), true);
  }
}
